import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import Aside from "./Aside";
import axios from "axios";

const BillingHistory = () => {
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15); // Matches API's default per_page
  const [editLoading, setEditLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState({});
  const [pdfLoading, setPdfLoading] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams(location.search);
        const pageFromURL = parseInt(queryParams.get("page"), 10) || 1;
        const authData = localStorage.getItem("auth");
        const token = authData ? JSON.parse(authData).token : null;
        const id = authData ? JSON.parse(authData).id : null;

        if (!token || !id) {
          setError("User is not authenticated.");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/billing`,
          {
            params: {
              _token: token,
              user_id: id,
              page: pageFromURL,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status === 200) {
          const invoices = response.data.invoices;
          setBillingData(invoices.data || []);
          setCurrentPage(invoices.current_page);
          setTotalPages(invoices.last_page);
          setTotalEntries(invoices.total);
          setItemsPerPage(invoices.per_page); // Set dynamically from API
        } else if (response.data.status === 422) {
          setError(response.data.message || "Validation error.");
        } else if (response.data.status === 401) {
          navigate("/login");
        }
      } catch (err) {
        setError("Failed to fetch billing data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });
  };

  const handlePageChange = (page) => {
    if (page === currentPage || page < 1 || page > totalPages) return;
    setCurrentPage(page);
    navigate(`/billing?page=${page}`);
  };

  // Functions like editBill, viewBill, invoicePdf remain unchanged...

  const editBill = async () => {
    setEditLoading(true);
    try {
      const authData = localStorage.getItem("auth");
      const parsedAuth = authData ? JSON.parse(authData) : null;
      const token = parsedAuth?.token || null;
      const id = parsedAuth?.id || null;

      if (!token || !id) {
        setError("User is not authenticated.");
        setEditLoading(false);
        return;
      }
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/billing/edit-billing-invoice/${token}`;
      const response = await axios.post(
        apiUrl,
        {
          _token: token,
          user_id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { status, message, url } = response.data;
      if (status === 200 && url) {
        window.location.href = url;
      } else if (status === 422) {
        setError(message || "Validation error.");
      } else if (status === 401) {
        navigate("/login");
      } else {
        setError("An unexpected error occurred.");
      }
    } catch (err) {
      console.error("Error in editBill:", err);
      setError("Failed to fetch billing data. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  const viewBill = async (invoice_id) => {
    setViewLoading((prevState) => ({ ...prevState, [invoice_id]: true }));
    try {
      const authData = localStorage.getItem("auth");
      const parsedAuth = authData ? JSON.parse(authData) : null;
      const token = parsedAuth?.token || null;
      const id = parsedAuth?.id || null;

      if (!token || !invoice_id) {
        setError("User is not authenticated.");
        setViewLoading((prevState) => ({ ...prevState, [invoice_id]: false }));
        return;
      }
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/billing/view-invoice/${invoice_id}`;
      const response = await axios.post(
        apiUrl,
        {
          _token: token,
          user_id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { status, message, hosted_invoice_url } = response.data;
      if (status === 200 && hosted_invoice_url) {
        window.open(hosted_invoice_url, "_blank");
      } else if (status === 422) {
        setError(message || "Validation error.");
      } else if (status === 401) {
        navigate("/login");
      } else {
        setError("An unexpected error occurred.");
      }
    } catch (err) {
      console.error("Error in viewBill:", err);
      setError("Failed to fetch billing data. Please try again.");
    } finally {
      setViewLoading((prevState) => ({ ...prevState, [invoice_id]: false }));
    }
  };

  const invoicePdf = async (invoice_id) => {
    setPdfLoading((prevState) => ({ ...prevState, [invoice_id]: true }));
    try {
      const authData = localStorage.getItem("auth");
      const parsedAuth = authData ? JSON.parse(authData) : null;
      const token = parsedAuth?.token || null;
      const id = parsedAuth?.id || null;

      if (!token || !invoice_id) {
        setError("User is not authenticated.");
        setPdfLoading((prevState) => ({ ...prevState, [invoice_id]: false }));
        return;
      }
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/billing/invoice-pdf/${invoice_id}`;
      const response = await axios.post(
        apiUrl,
        {
          _token: token,
          user_id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { status, message, invoice_pdf } = response.data;
      if (status === 200 && invoice_pdf) {
        window.location.href = invoice_pdf;
      } else if (status === 422) {
        setError(message || "Validation error.");
      } else if (status === 401) {
        navigate("/login");
      } else {
        setError("An unexpected error occurred.");
      }
    } catch (err) {
      console.error("Error in invoicePdf:", err);
      setError("Failed to fetch billing data. Please try again.");
    } finally {
      setPdfLoading((prevState) => ({ ...prevState, [invoice_id]: false }));
    }
  };

  return (
    <div>
      <Header />
      <div style={{ display: "flex" }}>
        <div
          style={{
            zIndex: "101",
            background: "white",
            boxShadow: "0 .8rem 1rem rgba(0, 0, 0, .15)",
            position: "fixed",
            height: "100vh",
          }}
        >
          <Aside />
        </div>
        <div className="mainstylle">
          <section className="generate-main paddtop">
            <div className="container py-4 px-2">
              <div className="row histroy">
                <div className="col-md-12">
                  <h1 className="text-left fw-bold pb-5 text-dark heading-50">
                    Billing History
                  </h1>
                  <div className="card rounded-4">
                    <div
                      className="card-header card-header-stretch border-bottom border-gray-200 d-flex justify-content-between align-items-center"
                      style={{
                        background: "white",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      <div className="card-title">
                        <h3 className="fw-bold m-0">Billing History</h3>
                      </div>
                      <div className="card-title">
                        <button
                          className="btn btn-theme btn-shadow fw-semibold edit-billing-btn"
                          onClick={editBill}
                          style={{ backgroundColor: "#1179fc" }}
                          disabled={editLoading}
                        >
                          {editLoading ? (
                            <>
                              Please wait...{" "}
                              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </>
                          ) : (
                            "Edit Billing"
                          )}
                        </button>
                      </div>
                    </div>
                    <div
                      id="kt_billing_months"
                      className="card-body p-0 tab-pane fade show active"
                      role="tabpanel"
                      aria-labelledby="kt_billing_months"
                    >
                      <div className="table-responsive">
                        <table className="table table-row-bordered align-middle gy-4 gs-9">
                          <thead className="border-bottom border-gray-200 fs-6 text-gray-600 fw-bold bg-light bg-opacity-75">
                            <tr>
                              <th>#</th>
                              <th>Date</th>
                              <th>Plan</th>
                              <th>Subtotal</th>
                              <th>Coupon Code</th>
                              <th>Discount</th>
                              <th>Total</th>
                              <th>Status</th>
                              <th>Invoice</th>
                            </tr>
                          </thead>
                          <tbody className="social_media_billing">
                            {loading ? (
                              <tr>
                                <td colSpan="9" style={{ textAlign: "center" }}>
                                  Loading...{" "}
                                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </td>
                              </tr>
                            ) : billingData.length === 0 ? (
                              <tr>
                                <td colSpan="9" style={{ textAlign: "center" }}>
                                  No Billing History available.
                                </td>
                              </tr>
                            ) : (
                              billingData.map((item, index) => {
                                const rowNumber =
                                  (currentPage - 1) * itemsPerPage + index + 1;
                                return (
                                  <tr key={item.id}>
                                    <td>{rowNumber}</td>
                                    <td>{formatDate(item.created_at)}</td>
                                    <td>{item.d_plan}</td>
                                    <td>{item.d_subtotal}</td>
                                    <td>{item.d_coupon || "N/A"}</td>
                                    <td>{item.d_discount}</td>
                                    <td>{item.d_total}</td>
                                    <td>
                                      <span
                                        className={`fs-7 fw-bold badge ${item.payment_status === "paid"
                                          ? "badge-light-success"
                                          : "badge-light-danger"
                                          }`}
                                      >
                                        {item.payment_status}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <a
                                          className="btn btn-sm btn-light btn-active-light-primary"
                                          href="#"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            viewBill(item.invoice_id);
                                          }}
                                          disabled={viewLoading[item.invoice_id]}
                                        >
                                          {viewLoading[item.invoice_id] ? (
                                            <span className="spinner-border spinner-border-sm align-middle"></span>
                                          ) : (
                                            "View"
                                          )}
                                        </a>
                                        <a
                                          className="btn btn-sm btn-theme btn-shadow"
                                          href="#"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            invoicePdf(item.invoice_id);
                                          }}
                                          disabled={pdfLoading[item.invoice_id]}
                                        >
                                          {pdfLoading[item.invoice_id] ? (
                                            <span className="spinner-border spinner-border-sm align-middle"></span>
                                          ) : (
                                            "PDF"
                                          )}
                                        </a>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                          <tr></tr>
                        </table>
                      </div>
                      {!loading && totalEntries > 0 && (
                        <div>
                          <p style={{ paddingLeft: "20px" }}>
                            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                            {Math.min(currentPage * itemsPerPage, totalEntries)}{" "}
                            of {totalEntries} entries
                          </p>
                          <div className="d-flex justify-content-center mt-2 pb-5">
                            <nav aria-label="Page navigation">
                              <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    aria-label="Previous"
                                  >
                                    « Previous
                                  </button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => {
                                  const page = index + 1;
                                  return (
                                    <li
                                      className={`page-item ${currentPage === page ? "active" : ""}`}
                                      key={page}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() => handlePageChange(page)}
                                      >
                                        {page}
                                      </button>
                                    </li>
                                  );
                                })}
                                <li
                                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    aria-label="Next"
                                  >
                                    Next »
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;